import React, { useState, useEffect } from "react";
import apiService from "../../config";
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from "../../StoreContext/LanguageContext";

const CreateHighwayProposal = ({ projectId }) => {
  const { language, translations } = useLanguage();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [ProjectName, SetProjectName] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [showProposalForm, setShowProposalForm] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [existingProposalId, setExistingProposalId] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Location dropdowns state
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [areaUnits, setAreaUnits] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);

  const [proposalData, setProposalData] = useState({
    proposalName: '',
    proposalDate: ''
  });

  // Project Id useEffect - Gets project information from localStorage
  useEffect(() => {
    const getProjectInfo = () => {

      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return {
            id: project.project_id || project.id,
            name: project.project_name || project.name
          };
        } catch (e) {
          console.error('Error parsing selected project (selectedProject):', e);
        }
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          const projectId = user.data?.project_id || user.project_id;
          const projectName = user.data?.project_name || user.project_name;
          return {
            id: projectId,
            name: projectName
          };
        } catch (e) {
          console.error('Error parsing user data (userData):', e);
        }
      }
      return { id: null, name: null };
    };

    const projectInfo = getProjectInfo();
    setCurrentProjectId(projectInfo.id);
    SetProjectName(projectInfo.name);
    console.log('Current Project Info:', projectInfo);
  }, []);

  // Load initial data
  useEffect(() => {
    loadDistricts();
    loadAreaUnits();
  }, []);

  const validateAreaFields = (totalArea, acquiredArea) => {
    const total = parseFloat(totalArea) || 0;
    const acquired = parseFloat(acquiredArea) || 0;

    if (total > 0 && acquired > 0 && acquired > total) {
      return {
        isValid: false,
        message: "संपादन क्षेत्र एकूण क्षेत्रापेक्षा जास्त असू शकत नाही"
      };
    }
    return { isValid: true };
  };
  // Load Districts
  const loadDistricts = async () => {
    setLoadingDistricts(true);
    try {
      const response = await apiService.getDistricts();
      if (response && response.status === 200) {
        setDistricts(response.data || []);
      }
    } catch (error) {
      console.error('Error loading districts:', error);
      setError('जिल्हे लोड करण्यात अयशस्वी');
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Load Area Units
  const loadAreaUnits = async () => {
    try {
      const response = await apiService.getAreaUnits();
      if (response && response.status === 200) {
        setAreaUnits(response.data || []);
      }
    } catch (error) {
      console.error('Error loading area units:', error);
      setError('क्षेत्रफळ युनिट लोड करण्यात अयशस्वी');
    }
  };

  // Load Talukas when district changes
  const loadTalukas = async (districtName) => {
    if (!districtName) {
      setTalukas([]);
      setVillages([]);
      return;
    }

    setLoadingTalukas(true);
    try {
      const response = await apiService.getTalukas(districtName);
      if (response && response.status === 200) {
        setTalukas(response.data || []);
      }
    } catch (error) {
      console.error('Error loading talukas:', error);
      setError('तालुके लोड करण्यात अयशस्वी');
    } finally {
      setLoadingTalukas(false);
    }
  };

  // Load Villages when district and taluka changes
  const loadVillages = async (districtName, talukaName) => {
    if (!districtName || !talukaName) {
      setVillages([]);
      return;
    }

    setLoadingVillages(true);
    try {
      const response = await apiService.getVillages(districtName, talukaName);
      if (response && response.status === 200) {
        setVillages(response.data || []);
      }
    } catch (error) {
      console.error('Error loading villages:', error);
      setError('गावे लोड करण्यात अयशस्वी');
    } finally {
      setLoadingVillages(false);
    }
  };

  // Handle district selection
  const handleDistrictChange = (districtName) => {
    setFormData(prev => ({
      ...prev,
      dist_name: districtName,
      taluka_name: '',
      village_name: ''
    }));

    // Reset dependent dropdowns
    setTalukas([]);
    setVillages([]);

    // Load talukas for selected district
    if (districtName) {
      loadTalukas(districtName);
    }
  };

  // Handle taluka selection
  const handleTalukaChange = (talukaName) => {
    setFormData(prev => ({
      ...prev,
      taluka_name: talukaName,
      village_name: ''
    }));

    // Reset villages
    setVillages([]);

    // Load villages for selected district and taluka
    if (formData.dist_name && talukaName) {
      loadVillages(formData.dist_name, talukaName);
    }
  };

  // Handle village selection
  const handleVillageChange = (villageName) => {
    setFormData(prev => ({
      ...prev,
      village_name: villageName
    }));
  };

  // Handle input changes for regular form fields
  const handleInputChange = (fieldName, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [fieldName]: value
      };

      // Validate area fields when either total_area or aquired_area changes
      if (fieldName === 'total_area' || fieldName === 'aquired_area') {
        const totalArea = fieldName === 'total_area' ? value : prev.total_area;
        const acquiredArea = fieldName === 'aquired_area' ? value : prev.aquired_area;

        const validation = validateAreaFields(totalArea, acquiredArea);
        if (!validation.isValid) {
          alert(validation.message);
          // Optionally, you can prevent the update by returning prev instead of newData
          // return prev;
        }
      }

      return newData;
    });
  };

  // Handle input changes for proposal form fields
  const handleProposalInputChange = (fieldName, value) => {
    setProposalData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Format date for display (DD/MM/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };


  // Updated handleFileUpload function with comprehensive validation and dynamic error messages
  const handleFileUpload = (event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      let allowedTypes;
      let allowedExtensions;
      let sizeLimit;
      let fileTypeDescription;

      // All fields accept the same file types: MP4, PDF, Word, PNG, JPG, JPEG
      allowedTypes = [
        "video/mp4",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/jpg",
        "image/png"
      ];
      allowedExtensions = ["mp4", "pdf", "doc", "docx", "png", "jpg", "jpeg"];

      // Set size limit based on file type
      const fileExtension = file.name.toLowerCase().split('.').pop();
      if (fileExtension === "mp4") {
        sizeLimit = 20 * 1024 * 1024; // 20 MB for videos
      } else {
        sizeLimit = 5 * 1024 * 1024; // 5 MB for other files
      }

      fileTypeDescription = "MP4, PDF, Word Document, PNG, JPG किंवा JPEG";

      // Get file extension
      // const fileExtension = file.name.toLowerCase().split('.').pop();

      // Validate file extension
      if (!allowedExtensions.includes(fileExtension)) {
        alert(`कृपया फक्त ${fileTypeDescription} फाइल अपलोड करा`);
        return;
      }

      // Validate MIME type (with fallback for extension-based validation)
      const isValidMimeType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (!isValidMimeType && !isValidExtension) {
        alert(`कृपया फक्त ${fileTypeDescription} फाइल अपलोड करा`);
        return;
      }

      // Validate file size
      if (file.size > sizeLimit) {
        const sizeLimitMB = fileExtension === "mp4" ? "20MB" : "5MB";
        alert(`फाइल साइझ ${sizeLimitMB} पेक्षा कमी असावी`);
        return;
      }

      // Additional validation: Empty file check
      if (file.size === 0) {
        alert("रिकामी फाइल अपलोड करू शकत नाही");
        return;
      }

      // Specific file type validations
      if (fileExtension === "pdf" && file.type !== "application/pdf") {
        alert("अपलोड केलेली फाइल वैध PDF नाही");
        return;
      }

      if (["png", "jpg", "jpeg"].includes(fileExtension) && !file.type.startsWith("image/")) {
        alert("अपलोड केलेली फाइल वैध इमेज नाही");
        return;
      }

      if (["doc", "docx"].includes(fileExtension) &&
        !["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(file.type)) {
        // Some browsers don't recognize Word MIME types correctly, so we'll be lenient here
        console.warn("Word document MIME type validation skipped due to browser compatibility");
      }

      if (fileExtension === "mp4" && file.type !== "video/mp4") {
        alert("अपलोड केलेली फाइल वैध MP4 व्हिडिओ नाही");
        return;
      }

      // Store the file with the correct field name
      const fileFieldName = `${fieldName}_file`;
      setFormData((prev) => ({
        ...prev,
        [fileFieldName]: file,
      }));

      console.log(`✓ File uploaded successfully for field: ${fieldName}`, file.name);
    }
  };

  // Create proposal and show main form
  const handleCreateProposal = async () => {
    if (!proposalData.proposalName.trim()) {
      alert('कृपया प्रस्ताव नाम प्रविष्ट करा');
      return;
    }
    if (!proposalData.proposalDate) {
      alert('कृपया प्रस्ताव दिनांक निवाडा');
      return;
    }

    // Generate proposal ID and show the form
    const generatedProposalId = `PROPOSAL-${Date.now().toString().slice(-4)}`;
    setProposalId(generatedProposalId);
    setShowProposalForm(false);
    alert("प्रस्ताव तयार केला गेला! आता फॉर्म भरा.");
  };

  // Row numbering helper
  const getSrNo = (idx) => {
    // Handles Sr. No. 1-9
    if (idx < 9) {
      return idx + 1;
    }
    // Handles Sr. No. 10 for questions 10a and 10b
    if (idx === 9 || idx === 10) {
      return 10;
    }
    // Handles Sr. No. 11-16
    if (idx > 10 && idx < 17) {
      return idx;
    }
    // Handles Sr. No. 17 for questions 17a and 17b
    if (idx === 17 || idx === 18) {
      return 17;
    }
    // Handles all subsequent Sr. Nos. (18 onwards)
    if (idx > 18) {
      return idx - 1;
    }
    // Default case (should not be reached with your form structure)
    return idx;
  };

  // Submit the complete form
  const handleSubmit = async () => {

    const selectFields = [
      { field: 'schedule_6_08', rowNum: 8 },
      { field: 'schedule_4_09', rowNum: 9 },
      { field: 'extracts_7_12_10', rowNum: '10अ' },
      { field: 'drone_kml', rowNum: '10ब' },
      { field: 'demarcation_11', rowNum: 11 },
      { field: 'acquisition_matter_12', rowNum: 12 },
      { field: 'taba_14', rowNum: 14 },
      { field: 'other_govt_land_15', rowNum: 15 },
      { field: 'near_railway_boundaries_16', rowNum: 16 },
      { field: 'religious_places_17', rowNum: '17अ' },
      { field: 'any_obstruction_18', rowNum: 18 },
      { field: 'road_obstruction_19', rowNum: 19 },
      { field: 'private_buildings_20', rowNum: 20 },
    ];

    // UPDATED: Check if all select fields are filled
    for (let selectField of selectFields) {
      if (!formData[selectField.field] || (formData[selectField.field] !== "होय" && formData[selectField.field] !== "नाही")) {
        setError(`कृपया प्रश्न क्रमांक ${selectField.rowNum} साठी "होय" किंवा "नाही" निवडा`);
        alert(`कृपया प्रश्न क्रमांक ${selectField.rowNum} साठी "होय" किंवा "नाही" निवडा`);
        return;
      }
    }
    const fieldsRequiringUpload = [
      { field: 'schedule_6_08', rowNum: 8, uploadRule: 'onlyForHoy' },
      { field: 'schedule_4_09', rowNum: 9, uploadRule: 'onlyForHoy' },
      { field: 'extracts_7_12_10', rowNum: '10अ', uploadRule: 'onlyForHoy' },
      { field: 'drone_kml', rowNum: '10ब', uploadRule: 'onlyForHoy' },
      { field: 'demarcation_11', rowNum: 11, uploadRule: 'onlyForHoy' },
      { field: 'acquisition_matter_12', rowNum: 12, uploadRule: 'forBoth' }, // Both होय and नाही need files
      { field: 'taba_14', rowNum: 14, uploadRule: 'onlyForHoy' },
      { field: 'other_govt_land_15', rowNum: 15, uploadRule: 'onlyForHoy' },
      { field: 'near_railway_boundaries_16', rowNum: 16, uploadRule: 'onlyForHoy' },
      { field: 'religious_places_17', rowNum: '17अ', uploadRule: 'forBoth' }, // Both होय and नाही need files
      { field: 'any_obstruction_18', rowNum: 18, uploadRule: 'onlyForHoy' },
      { field: 'road_obstruction_19', rowNum: 19, uploadRule: 'onlyForHoy' },
      { field: 'private_buildings_20', rowNum: 20, uploadRule: 'onlyForHoy' }
    ];


    // Updated validation loop:
    for (let fieldInfo of fieldsRequiringUpload) {
      const fieldValue = formData[fieldInfo.field];
      const fileUploaded = formData[`${fieldInfo.field}_file`];

      if (fieldInfo.uploadRule === 'forBoth') {
        // For fields that require upload for both "होय" and "नाही"
        if ((fieldValue === "होय" || fieldValue === "नाही") && !fileUploaded) {
          let errorMessage;
          if (fieldInfo.field === 'acquisition_matter_12') {
            if (fieldValue === "होय") {
              errorMessage = `कृपया प्रश्न क्रमांक ${fieldInfo.rowNum} साठी यापूर्वी सुरू केलेल्या भूसंपादन प्रक्रियेचे संबंधित कागदपत्र अपलोड करा`;
            } else {
              errorMessage = `कृपया प्रश्न क्रमांक ${fieldInfo.rowNum} साठी यापूर्वी भूसंपादन प्रक्रिया सुरू केलेली नसल्याचे प्रमाणपत्र अपलोड करा`;
            }
          } else if (fieldInfo.field === 'religious_places_17') {
            if (fieldValue === "होय") {
              errorMessage = `कृपया प्रश्न क्रमांक ${fieldInfo.rowNum} साठी धार्मिक स्थळांची यादी व त्यांचे फोटो अपलोड करा`;
            } else {
              errorMessage = `कृपया प्रश्न क्रमांक ${fieldInfo.rowNum} साठी धार्मिक स्थळे नसल्याचे प्रमाणपत्र अपलोड करा`;
            }
          }

          setError(errorMessage);
          alert(errorMessage);
          return;
        }
      } else if (fieldInfo.uploadRule === 'onlyForHoy') {
        // For fields that only require upload when "होय" is selected
        if (fieldValue === "होय" && !fileUploaded) {
          const errorMessage = `कृपया प्रश्न क्रमांक ${fieldInfo.rowNum} साठी संबंधित कागदपत्र अपलोड करा`;
          setError(errorMessage);
          alert(errorMessage);
          return;
        }
      }
    }

    // Step 3: Area validation
    const areaValidation = validateAreaFields(formData.total_area, formData.aquired_area);
    if (!areaValidation.isValid) {
      setError(areaValidation.message);
      alert(areaValidation.message);
      return;
    }

    // Step 4: Required fields validation
    if (!currentProjectId) {
      const errorMessage = "प्रकल्प ID अनुपलब्ध आहे";
      setError(errorMessage);
      alert(errorMessage);
      return;
    }

    if (!proposalData?.proposalDate) {
      const errorMessage = "प्रस्ताव दिनांक अनुपलब्ध आहे";
      setError(errorMessage);
      alert(errorMessage);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Validate and format date
      const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!datePattern.test(proposalData.proposalDate)) {
        throw new Error("अवैध दिनांक स्वरूप. कृपया DD/MM/YYYY स्वरूपात दिनांक प्रविष्ट करा");
      }

      const [day, month, year] = proposalData.proposalDate.split('/');
      const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

      // Validate formatted date
      const dateObj = new Date(formattedDate);
      if (isNaN(dateObj.getTime())) {
        throw new Error("अवैध दिनांक. कृपया योग्य दिनांक प्रविष्ट करा");
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();

      // Add basic fields
      formDataToSend.append('project_id', currentProjectId);
      formDataToSend.append('acquisition_proposal_date', formattedDate);
      formDataToSend.append('acquisition_proposal_no', proposalData.proposalName);

      // Convert Marathi values to bit values for backend
      const convertToBitValue = (value) => {
        if (value === "होय") return "1";
        if (value === "नाही") return "0";
        return value;
      };

      // Fields that need conversion from Marathi to bit values
      const fieldsToConvert = [
        'acquisition_regional_incharge_designation',
        'schedule_6_08',
        'schedule_4_09',
        'extracts_7_12_10',
        'drone_kml',
        'demarcation_11',
        'acquisition_matter_12',
        'taba_14',
        'other_govt_land_15',
        'near_railway_boundaries_16',
        'religious_places_17',
        'any_obstruction_18',
        'road_obstruction_19',
        'private_buildings_20',
      ];

      // Add form fields and files with conversion
      Object.keys(formData).forEach(key => {
        if (key.endsWith('_file')) {
          // This is a file - append directly
          console.log(`Adding file: ${key}`, formData[key].name);
          formDataToSend.append(key, formData[key]);
        } else {
          // Convert bit fields, keep others as is
          const value = fieldsToConvert.includes(key)
            ? convertToBitValue(formData[key])
            : formData[key];
          formDataToSend.append(key, value || '');
        }
      });

      console.log('Submitting Namuna K data with files');

      // Log form data for debugging
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
      }
      formDataToSend.append("isactive", 1);
      const response = await apiService.postAddHighwayProposal(formDataToSend);
      console.log('Roshani :', response);
      if (response && (response.status === 200 || response.status === 201)) {
        alert("नमुना क यशस्वीरीत्या जतन केला आहे.");
        window.location.reload();
      } else {
        setError(response?.message || "Save failed");
      }
    } catch (err) {
      console.error("Error saving data:", err);

      // Handle different types of errors
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError("नेटवर्क त्रुटी. कृपया तुमचे इंटरनेट कनेक्शन तपासा");
      } else if (err.name === 'AbortError') {
        setError("विनंती रद्द केली गेली");
      } else if (err.code === 'NETWORK_ERROR') {
        setError("नेटवर्क त्रुटी. कृपया तुमचे इंटरनेट कनेक्शन तपासा");
      } else if (err.message.includes('timeout')) {
        setError("विनंती वेळेत पूर्ण झाली नाही. कृपया पुन्हा प्रयत्न करा");
      } else {
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "डेटा जतन करण्यात अयशस्वी";
        setError(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '16px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      fontFamily: "Arial, sans-serif",
      maxWidth: "800px",
      margin: "0 auto"
    }}>

      {/* Proposal Creation Form */}
      {showProposalForm && (
        <div style={{
          border: "2px solid #2196F3",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#f8f9fa"
        }}>
          <h3 style={{ textAlign: "center", marginBottom: "20px", color: "#2196F3" }}>
            नमुना "क" प्रस्ताव तयार करा
          </h3>
          <div style={{ display: "grid", gap: "15px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                प्रस्ताव नाव (Proposal No) *
              </label>
              <input
                type="text"
                value={proposalData.proposalName}
                onChange={(e) => handleProposalInputChange('proposalName', e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px"
                }}
                placeholder="प्रस्तावाचे क्रमांक प्रविष्ट करा"
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                प्रस्ताव दिनांक (Proposal Date) *
              </label>
              <input
                type="date"
                value={formatDateForInput(proposalData.proposalDate)}
                onChange={(e) => {
                  const formattedDate = formatDateForDisplay(e.target.value);
                  handleProposalInputChange('proposalDate', formattedDate);
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px"
                }}
              />
              {proposalData.proposalDate && (
                <small style={{ color: "#666", fontSize: "12px" }}>
                  निवडलेली दिनांक: {proposalData.proposalDate}
                </small>
              )}
            </div>
            <button
              onClick={handleCreateProposal}
              disabled={saving}
              style={{
                backgroundColor: saving ? "#ccc" : "#2196F3",
                color: "white",
                padding: "12px 24px",
                border: "none",
                borderRadius: "4px",
                fontSize: "16px",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: "bold"
              }}
            >
              {saving ? "प्रस्ताव तयार करत आहे..." : "प्रस्ताव तयार करा आणि पुढे जा"}
            </button>
          </div>
        </div>
      )}

      {/* Download Button at Top */}
      {/* {!showProposalForm && (
        <div style={{
          textAlign: "right",
          marginBottom: "20px"
        }}>
          <button
            onClick={handleDownload}
            style={{
              backgroundColor: "#4CAF50",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            📄 Print
          </button>
        </div>
      )} */}

      {/* Main Form - Only show when proposal is created */}
      {!showProposalForm && (
        <>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <h2 style={{ marginBottom: "10px", fontSize: "18px", fontWeight: "bold" }}>
              नमुना "क"
            </h2>
            <p class="text-xs m-0 leading-tight mb-5 text-center">
              {ProjectName || 'N/A'} साठी खाजगी जमिनीच्या भूसंपादचा प्रस्ताव

            </p>
          </div>

          {/* Project and proposal info */}
          <div style={{
            backgroundColor: "#e3f2fd",
            padding: "10px",
            borderRadius: "4px",
            marginBottom: "20px",
            fontSize: "12px",
            textAlign: "center"
          }}>
            <strong>{ }</strong> {ProjectName} |
            <strong> Proposal No:</strong> {proposalData.proposalName} |
            <strong> Date:</strong> {proposalData.proposalDate}
          </div>
        </>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: "#ffebee",
          color: "#c62828",
          padding: "10px",
          borderRadius: "4px",
          marginTop: "10px",
          border: "1px solid #ef5350",
          fontSize: "12px"
        }}>
          {error}
        </div>
      )}

      {/* Main Form Table - Only show when proposal is created */}
      {!showProposalForm && (
        <>
          {/* Table */}
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "2px solid #000",
            fontSize: "12px"
          }}>
            <tbody>
              {formRows.map((row, idx) => (
                <tr key={idx}
                  className="border border-black hover:bg-black-50"
                >
                  {![10, 18].includes(idx) && (
                    <td
                      className="border border-black px-4 py-3 font-medium align-top text-center"
                      // For main questions (10a and 17a), we add a rowspan of 2
                      rowSpan={(idx === 9 || idx === 17) ? 2 : 1}
                    >
                      {getSrNo(idx)}
                    </td>
                  )}
                  <td className="border border-black px-4 py-3 font-medium align-top">
                    {row.question}
                  </td>
                  <td style={{ border: "1px solid #000", padding: "10px", verticalAlign: "top" }}>
                    {row.type === "text" && (
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          backgroundColor: "transparent",
                          padding: "2px",
                          fontSize: "12px"
                        }}
                        value={formData[row.fieldName] || ""}
                        onChange={(e) => handleInputChange(row.fieldName, e.target.value)}
                        placeholder="माहिती प्रविष्ट करा"
                      />
                    )}

                    {row.type === "textarea" && (
                      <textarea
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          backgroundColor: "transparent",
                          padding: "2px",
                          fontSize: "12px",
                          minHeight: "60px",
                          resize: "vertical",
                          fontFamily: "Arial, sans-serif"
                        }}
                        rows={row.rows || 3}
                        value={formData[row.fieldName] || ""}
                        onChange={(e) => handleInputChange(row.fieldName, e.target.value)}
                        placeholder="तपशीलवार माहिती प्रविष्ट करा"
                      />
                    )}

                    {row.type === "date" && (
                      <input
                        type="date"
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          backgroundColor: "transparent",
                          padding: "2px",
                          fontSize: "12px"
                        }}
                        value={formData[row.fieldName] || ""}
                        onChange={(e) => handleInputChange(row.fieldName, e.target.value)}
                      />
                    )}

                    {row.type === "select" && (
                      <>
                        <select
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            backgroundColor: "transparent",
                            padding: "2px"
                          }}
                          value={formData[row.fieldName] || ""}
                          onChange={(e) => handleInputChange(row.fieldName, e.target.value)}
                        >
                          <option value="">निवाडा</option>
                          {row.options.map((option, i) => (
                            <option key={i} value={option.value}>{option.label}</option>
                          ))}
                        </select>

                        {/* FIXED: Show upload button if "हो" is selected and showUpload is true */}
                        {((row.showUpload === true && formData[row.fieldName] === "होय") ||
                          (row.showUpload === "both" && (formData[row.fieldName] === "होय" || formData[row.fieldName] === "नाही"))) && (
                            <div style={{ marginTop: "10px" }}>
                              <input
                                type="file"
                                onChange={(e) => handleFileUpload(e, row.fieldName)} // FIXED: Pass fieldName
                                accept={row.fieldName === "drone_kml" ? "video/*" : ".pdf,.jpg,.jpeg,.png,.doc,.docx"}
                                style={{ fontSize: "12px" }}
                                required
                              />
                              <div style={{ fontSize: "10px", color: "#666", marginTop: "2px" }}>
                                {row.fieldName === "drone_kml"
                                  ? "व्हिडिओ (20MB पर्यंत) अपलोड करणे आवश्यक आहे"
                                  : "कागदपत्र अपलोड करणे आवश्यक आहे"}
                              </div>
                              {/* FIXED: Show uploaded file name */}
                              {formData[`${row.fieldName}_file`] && (
                                <div style={{ fontSize: "10px", color: "green", marginTop: "2px" }}>
                                  ✓ अपलोड केले: {formData[`${row.fieldName}_file`].name}
                                </div>
                              )}
                            </div>
                          )}
                      </>
                    )}

                    {row.type === "static" && (
                      <div style={{ fontSize: "12px", whiteSpace: "pre-line", lineHeight: "1.4" }}>
                        {row.value}
                      </div>
                    )}

                    {row.type === "multiple_fields" && (
                      <div style={{ display: "grid", gap: "8px" }}>
                        {row.fields.map((field, fieldIndex) => (
                          <div key={fieldIndex} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <label style={{
                              display: "block",
                              fontSize: "11px",
                              fontWeight: "bold",
                              marginBottom: "3px",
                              color: "#333"
                            }}>
                              {field.label}:
                            </label>

                            {field.type === "textarea" ? (
                              <textarea
                                style={{
                                  width: "100%",
                                  border: "1px solid #ddd",
                                  outline: "none",
                                  backgroundColor: "#fff",
                                  padding: "6px",
                                  fontSize: "12px",
                                  borderRadius: "3px"
                                }}
                                rows={field.rows || 2}
                                value={formData[field.fieldName] || ""}
                                onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                                placeholder={field.placeholder}
                              />
                            ) : field.type === "area_unit_dropdown" ? (
                              <select
                                style={{
                                  width: "100%",
                                  border: "1px solid #ddd",
                                  outline: "none",
                                  backgroundColor: "#fff",
                                  padding: "6px",
                                  fontSize: "12px",
                                  borderRadius: "3px"
                                }}
                                value={formData[field.fieldName] || ""}
                                onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                              >
                                <option value="">क्षेत्रफळ युनिट निवडा</option>
                                {areaUnits.map((unit) => (
                                  <option key={unit.area_unit_id} value={unit.unit_name}>
                                    {unit.unit_name} ({unit.unit_code})
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={field.type}
                                style={{
                                  width: "100%",
                                  border: "1px solid #ddd",
                                  outline: "none",
                                  backgroundColor: "#fff",
                                  padding: "6px",
                                  fontSize: "12px",
                                  borderRadius: "3px"
                                }}
                                value={formData[field.fieldName] || ""}
                                onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                                placeholder={field.placeholder}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}


                    {/* Location Multiple Dropdowns */}
                    {row.type === "multiple_dropdowns" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

                        {/* District */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <label>जिल्हा</label>
                          <select
                            style={{
                              width: "100%",
                              border: "1px solid #ddd",
                              outline: "none",
                              backgroundColor: "#fff",
                              padding: "6px",
                              fontSize: "12px",
                              borderRadius: "3px"
                            }}
                            value={formData.dist_name || ""}
                            onChange={(e) => handleDistrictChange(e.target.value)}
                            disabled={loadingDistricts}
                          >
                            <option value="">{loadingDistricts ? "जिल्हे लोड होत आहेत..." : "जिल्हा निवडा"}</option>
                            {districts.map((district) => (
                              <option key={district.dist_no} value={district.dist_name}>
                                {district.dist_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Taluka */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <label>तालुका</label>
                          <select
                            style={{
                              width: "100%",
                              border: "1px solid #ddd",
                              outline: "none",
                              backgroundColor: "#fff",
                              padding: "6px",
                              fontSize: "12px",
                              borderRadius: "3px"
                            }}
                            value={formData.taluka_name || ""}
                            onChange={(e) => handleTalukaChange(e.target.value)}
                            disabled={loadingTalukas || !formData.dist_name}
                          >
                            <option value="">
                              {!formData.dist_name
                                ? "पहिले जिल्हा निवडा"
                                : loadingTalukas
                                  ? "तालुके लोड होत आहेत..."
                                  : "तालुका निवडा"}
                            </option>
                            {talukas.map((taluka) => (
                              <option key={taluka.taluka_no} value={taluka.taluka_name}>
                                {taluka.taluka_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Village */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <label>गाव</label>
                          <select
                            style={{
                              width: "100%",
                              border: "1px solid #ddd",
                              outline: "none",
                              backgroundColor: "#fff",
                              padding: "6px",
                              fontSize: "12px",
                              borderRadius: "3px"
                            }}
                            value={formData.village_name || ""}
                            onChange={(e) => handleVillageChange(e.target.value)}
                            disabled={loadingVillages || !formData.dist_name || !formData.taluka_name}
                          >
                            <option value="">
                              {!formData.dist_name || !formData.taluka_name
                                ? "पहिले जिल्हा आणि तालुका निवडा"
                                : loadingVillages
                                  ? "गावे लोड होत आहेत..."
                                  : "गाव निवडा"}
                            </option>
                            {villages.map((village) => (
                              <option key={village.village_id} value={village.village_name}>
                                {village.village_name}
                              </option>
                            ))}
                          </select>
                        </div>

                      </div>
                    )}

                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Save Button */}
          <div style={{
            textAlign: "center",
            marginTop: "20px",
            marginBottom: "20px"
          }}>
            <button
              onClick={handleSubmit}
              disabled={saving}
              style={{
                backgroundColor: saving ? "#ccc" : "#2196F3",
                color: "white",
                padding: "12px 24px",
                border: "none",
                borderRadius: "4px",
                fontSize: "14px",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: "bold"
              }}
            >
              {saving
                ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {translations[language].Sending}
                  </>
                ) : (
                  <>
                    {/* <ArrowLeft className="mr-2 h-4 w-4" /> */}
                    {translations[language].CommonSave}
                  </>
                )}
            </button>
          </div>

          {/* Footer Signatures */}
          <div style={{
            marginTop: "40px",
            display: "flex",
            justifyContent: "space-between",
            fontSize: "12px"
          }}>
            <div style={{ textAlign: "center", width: "45%" }}>
              {/* <div style={{ height: "60px", borderBottom: "1px solid #000", marginBottom: "5px" }}></div> */}
              <div>
                <div>{formData.acquisition_incharge_name || ''}</div>
                <div>{formData.acquisition_incharge_designation || ''}</div>
                {/* <div>(एरक्राझावत्र)</div>
                <div>कार्यकारी अधिकारी</div>
                <div>जिल्हा जग्याला सेवा उपविभाग क्र.२</div>
                <div>खादान</div>
                <div>"क" र)</div> */}
              </div>
            </div>
            <div style={{ textAlign: "center", width: "45%" }}>
              {/* <div style={{ height: "60px", borderBottom: "1px solid #000", marginBottom: "5px" }}></div> */}
              <div>
                <div>{formData.acquisition_regional_incharge_name || ''}</div>
                <div>{formData.acquisition_regional_incharge_designation || ''}</div>
                {/* <div>कार्यकारी अधिकारी</div>
                <div>जिल्हा उग्रा सेवा सैवभाग</div>
                <div>२९. खादान</div> */}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// FIXED: Updated formRows configuration with proper field mapping
const formRows = [
  // 1
  {
    question: "भूसंपादन प्रस्ताव पाठविणाऱ्या अधिका-याचे नाव,पदनाम व पत्ता",
    type: "multiple_fields",
    fields: [
      {
        label: "नाव",
        fieldName: "acquisition_incharge_name",
        type: "text",
        placeholder: "अधिकाऱ्याचे पूर्ण नाव प्रविष्ट करा"
      },
      {
        label: "पदनाम/संपादन संस्थेचे नाव",
        fieldName: "acquisition_incharge_designation",
        type: "text",
        placeholder: "पदनाम प्रविष्ट करा"
      },
      {
        label: "पत्ता",
        fieldName: "acquisition_incharge_address",
        type: "text",
        placeholder: "पत्ता प्रविष्ट करा"
      }
    ]
  },

  // 2
  {
    question: " भूसंपादन प्रस्ताव सादर करणाऱ्या प्रादेशिक विभागाचे / प्राधिकरणाचे / महामंडळाचे नाव व पत्ता",
    type: "text",
    fieldName: "acquisition_agency"
  },
  // 3
  {
    question: " मंत्रालयीन स्तरावरील संबंधीत प्रशासकीय विभाग",
    type: "text",
    fieldName: "acquisition_department"
  },

  // 4
  {
    question: " जमीन कोणत्या कारणासाठी संपादन करून हवी आहे ?",
    type: "text",
    fieldName: "acquisition_purpose"
  },

  // 5
  {
    question: "जमीन कोणत्या शासन धोरणानुसार/कायद्यानुसार संपादन करून हवी आहे ?",
    type: "text",
    fieldName: "acquisition_government_policy"
  },

  // 6 - Location Dropdowns (District, Taluka, Village)
  {
    question: "संपादन करावयाच्या जमिनी ज्या क्षेत्रात मोडतात ते गाव, तालुका, जिल्हा.",
    type: "multiple_dropdowns", // Custom type for multiple dropdowns in one row
    fields: [
      {
        label: "जिल्हा",
        fieldName: "dist_name",
        type: "district_dropdown" // custom type for rendering district select
      },
      {
        label: "तालुका",
        fieldName: "taluka_name",
        type: "taluka_dropdown" // custom type for rendering taluka select
      },
      {
        label: "गाव",
        fieldName: "village_name",
        type: "village_dropdown" // custom type for rendering village select
      }
    ]
  },

  // 7 - Area Fields (Grouped)
  {
    question: "संपादन करून हवे असलेले एकूण क्षेत्र",
    type: "multiple_fields",
    fields: [
      {
        label: "एकूण क्षेत्र",
        fieldName: "total_area",
        type: "text",
        placeholder: "एकूण क्षेत्र प्रविष्ट करा"
      },
      {
        label: "संपादन क्षेत्र",
        fieldName: "aquired_area", // fixed spelling
        type: "text",
        placeholder: "संपादन क्षेत्र प्रविष्ट करा"
      },
      {
        label: "क्षेत्रफळ युनिट",
        fieldName: "area_unit",
        type: "area_unit_dropdown"
      }
    ]
  },
  // 8
  {
    question: "गावनिहाय व गट/सर्व्हे क्र. नुसार आणि संपादन क्षेत्राच्या चतुःसीमा दाखविणारी अनुसूची यासोबत जोडणे आवश्यक आहे, त्याप्रमाणे ती जोडली आहे काय ?(परिशिष्ट ६)",
    type: "select",
    fieldName: "schedule_6_08",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },
  // 9
  {
    question: "संपादन करावयाच्या क्षेत्राचा चिन्हांकित / साक्षांकित नकाशा प्रस्तावासोबत  जोडणे आवश्यक आहे, त्या प्रमाणे जोडला आहे काय ? भूमिअभिलेख विभागाकडील गावाचे नकाशे दोन प्रतीत सादर केले आहे काय ?",
    type: "select",
    fieldName: "schedule_4_09",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },

  // 10अ - 7/12 extracts
  {
    question: "अ. संपादन करावयाच्या क्षेत्राचे ७/१२ चे उतारे किंवा मालमत्ता नोंदवहीतील उतारा प्रस्तावासोबत जोडणे आवश्यक आहे, त्याप्रमाणे जोडण्यात आलेले आहेत काय ?",
    type: "select",
    fieldName: "extracts_7_12_10",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },

  // 10ब - Drone + KML
  {
    question: "ब. संपादन करावयाच्या क्षेत्राचे ड्रोन‌द्वारे केलेले छायाचित्रण व KML फाईल जोडण्यात आलेले आहेत काय ?",
    type: "select",
    fieldName: "drone_kml",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },

  // 11
  {
    question: "संपादन करावयाच्या क्षेत्राचे सीमांकन करण्यात आले आहे काय ? असल्यास, सीमांकन प्रमाणपत्र सोबत जोडले आहे काय ?",
    type: "select",
    fieldName: "demarcation_11",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },

  // 12
  {
    question: "प्रस्तावामधील नमुद क्षेत्रासाठी यापूर्वीच थेट खरेदी / अन्य कायद्याच्या तरतुदीनुसार भूसंपादन प्रक्रिया सुरू करण्यात आलेली आहे काय ?(संपादनाबाबतचे प्रमाणपत्र)",
    type: "select",
    fieldName: "acquisition_matter_12",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: "both",
    showMessageForNahi: true
  },

  // 13
  {
    question: "क्र.१२ चे उत्तर - होय असल्यास, संबंधित भूसंपादन कायदा व प्रस्तावित क्षेत्र नमूद करावे.",
    type: "textarea",
    fieldName: "land_acquisition_law_13"
  },
  // 14
  {
    question: "गरजेनुसार आवश्यक तेवढेच क्षेत्र संपादन करून मागितले आहे काय ? त्याबाबत प्रमाणपत्र सोबत जोडावे.",
    type: "select",
    fieldName: "taba_14",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },
  // 15
  {
    question: "सबंधित प्रयोजनासाठी जवळपास कमी खर्चाची अन्य खाजगी किंवा सरकारी जमीन उपलब्ध आहे काय ? असल्यास, किती अंतरावर आहे ?",
    type: "select",
    fieldName: "other_govt_land_15",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },
  // 16
  {
    question: "संपादन करून मागितलेली जमीन रेल्वे सीमांच्या जवळपास आहे काय? असल्यास, किती अंतरावर आहे ?",
    type: "select",
    fieldName: "near_railway_boundaries_16",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },
  // 17a - Religious Places Presence Question
  {
    question: "अ. संपादन क्षेत्रात धार्मिक, प्रार्थना स्थळे, मंदिरे, कब्रस्थान व स्मशानभूमी किंवा पवित्र स्मारके येतात काय ? ",
    type: "select",
    fieldName: "religious_places_17",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: "both"
  },

  // 17ब - Religious Places Distance Question  
  {
    question: "ब. संपादन क्षेत्राच्या जवळपास अशा बाबी असल्यास, किती अंतरावर आहेत ?",
    type: "text",
    fieldName: "proximity_distance",
    placeholder: "अंतर मीटर/किलोमीटरमध्ये नमूद करा"
  },
  // 18
  {
    question: "संपादन करावयाच्या क्षेत्रातून कोणताही सार्वजनिक मार्गाधिकार, जलप्रवाह, जलवाहिनी किंवा जलनिःसारण मार्ग जातो काय ? किंवा खास काही अडथळा निर्माण होतो काय ?",
    type: "select",

    fieldName: "any_obstruction_18",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },
  // 19
  {
    question: "नागरी क्षेत्रात इमारतीमधील खाजगी प्रवेश मार्गाचे किंवा पूर्वापार प्रकाशमार्ग आणि / किंवा हायवेचे मार्ग यावरील कोणत्याही अधिकारात अडथळा येणेचा संभव आहे काय ?",
    type: "select",
    fieldName: "road_obstruction_19",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },
  // 20
  {
    question: "संपादन क्षेत्राच्या सभोवार इमारत किंवा कारखाना यांना किंवा त्यांची जागा छेदून जाते काय ?",
    type: "select",
    fieldName: "private_buildings_20",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ],
    showUpload: true
  },
  // 21
  {
    question: " प्रादेशिक विभाग/प्राधिकरण / महामंडळाच्या प्राधिकृत अधिकाऱ्याचा तपशील",
    type: "multiple_fields",
    fields: [
      {
        label: "नाव (Name)",
        fieldName: "acquisition_regional_incharge_name",
        type: "text",
        placeholder: "अधिकाऱ्याचे नाव प्रविष्ट करा"
      },
      {
        label: "पदनाम(Designation)",
        fieldName: "acquisition_regional_incharge_designation",
        type: "text",
        placeholder: "पदनाम प्रविष्ट करा"
      },
      {
        label: "पत्ता(Address)",
        fieldName: "acquisition_regional_incharge_address",
        type: "text",
        placeholder: "पत्ता प्रविष्ट करा"
      },

      {
        label: "भ्रमणध्वनी (Cell Phone)",
        fieldName: "acquisition_regional_incharge_contact",
        type: "number",
        placeholder: "मोबाईल नंबर प्रविष्ट करा",
        inputMode: "numeric",
        // pattern: "[0-9]*"
      },
      {
        label: "कार्यालयीन दुरध्वनी (Office Phone)",
        fieldName: "acquisition_regional_incharge_office_contact",
        type: "number",
        placeholder: "कार्यालयीन नंबर प्रविष्ट करा",
        inputMode: "numeric",
      },
      {
        label: "ईमेल (email-id)",
        fieldName: "acquisition_regional_incharge_email",
        type: "email",
        placeholder: "ईमेल पत्ता प्रविष्ट करा"
      },
    ]
  }
];

export default CreateHighwayProposal;